import React, { useState } from "react";
import { css } from "@emotion/css";

/**
 * Escape hatch shown below the quest list:
 *
 *   [None of these feel right? — get different ones]
 *
 * Clicking expands an optional textarea where the learner can give
 * one-line feedback.  The feedback is threaded into the next
 * quest_recommendation prompt so the LLM can steer.  Empty feedback
 * is OK — the request still regenerates a fresh set.
 */

const wrapperStyle = css`
  margin-top: 24px;
  padding: 16px;
  border: 1px dashed var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-subtle);
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const headerTextStyle = css`
  font-size: 0.9em;
  color: var(--color-text-secondary);
`;

const toggleButtonStyle = css`
  border: none;
  background: transparent;
  color: var(--color-primary);
  font-weight: 600;
  font-size: 0.88em;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;

  &:hover { background: var(--color-bg-raised); }
`;

const expandedStyle = css`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const promptStyle = css`
  font-size: 0.85em;
  color: var(--color-text-muted);
  font-style: italic;
`;

const textareaStyle = css`
  width: 100%;
  min-height: 60px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 10px;
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: inherit;
  font-size: 0.9em;
  line-height: 1.5;
  resize: vertical;
  box-sizing: border-box;

  &:focus { outline: none; border-color: var(--color-primary); }
  &::placeholder { color: var(--color-text-muted); }
`;

const buttonRowStyle = css`
  display: flex;
  gap: 8px;
`;

const submitBtnStyle = css`
  flex: 1;
  padding: 9px 14px;
  border: none;
  border-radius: 8px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) { background: var(--color-primary-hover); }
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;

const cancelBtnStyle = css`
  padding: 9px 14px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-bg-elevated);
  color: var(--color-text-secondary);
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: var(--color-bg-raised); }
`;

interface QuestEscapeHatchProps {
  isStreaming: boolean;
  onRegenerate: (feedback: string) => void;
}

export const QuestEscapeHatch: React.FC<QuestEscapeHatchProps> = ({
  isStreaming,
  onRegenerate,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [feedback, setFeedback] = useState("");

  const handleSubmit = () => {
    onRegenerate(feedback.trim());
    setExpanded(false);
    setFeedback("");
  };

  return (
    <div className={wrapperStyle} data-testid="quest-escape-hatch">
      <div className={headerStyle}>
        <span className={headerTextStyle}>None of these quite fit?</span>
        {!expanded && (
          <button
            className={toggleButtonStyle}
            onClick={() => setExpanded(true)}
            disabled={isStreaming}
            data-testid="quest-regen-toggle"
          >
            Get different ones →
          </button>
        )}
      </div>
      {expanded && (
        <div className={expandedStyle}>
          <div className={promptStyle}>
            Optional: what felt off, or what would land better? (One sentence is plenty —
            you can skip and just regenerate.)
          </div>
          <textarea
            className={textareaStyle}
            placeholder="e.g. I'd prefer something more applied / more theoretical / closer to NLP / shorter…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            data-testid="quest-regen-feedback"
          />
          <div className={buttonRowStyle}>
            <button
              className={cancelBtnStyle}
              onClick={() => {
                setExpanded(false);
                setFeedback("");
              }}
              disabled={isStreaming}
            >
              Cancel
            </button>
            <button
              className={submitBtnStyle}
              onClick={handleSubmit}
              disabled={isStreaming}
              data-testid="quest-regen-submit"
            >
              Regenerate options
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
